// Patch condiviso della History API: intercetta pushState/replaceState/popstate
// e notifica i subscriber a ogni cambio di URL. Il monkey-patch si installa UNA
// sola volta al primo subscriber e si ripristina quando l'ultimo si disiscrive —
// così breadcrumb di navigazione e pageview possono coesistere senza doppio
// patch. Estratto da `breadcrumbs.ts` (l'ex `installNavigation`) per condividerlo.

/// Notificato a ogni cambio URL: [to] è il nuovo href, [from] il precedente
/// (`null` se non ancora noto).
export type NavigationListener = (to: string, from: string | null) => void

const listeners = new Set<NavigationListener>()
let uninstallPatch: (() => void) | null = null
let lastHref = ''

/// Registra [listener] sui cambi di URL SPA (pushState/replaceState/popstate) e
/// ritorna la funzione di disiscrizione. Il patch della History è condiviso:
/// installato al primo subscriber, ripristinato quando esce l'ultimo.
export function subscribeNavigation(listener: NavigationListener): () => void {
  listeners.add(listener)
  if (uninstallPatch === null) uninstallPatch = installPatch()

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && uninstallPatch !== null) {
      uninstallPatch()
      uninstallPatch = null
    }
  }
}

function installPatch(): () => void {
  if (typeof window === 'undefined' || typeof history === 'undefined') {
    return noop
  }

  lastHref = safeHref()

  const record = (): void => {
    const to = safeHref()
    if (to === lastHref) return
    const from = lastHref
    lastHref = to
    // Copia difensiva: un listener potrebbe disiscriversi durante la notifica.
    for (const listener of [...listeners]) listener(to, from)
  }

  const originalPush = history.pushState
  const originalReplace = history.replaceState

  history.pushState = function patchedPush(
    this: History,
    ...args: Parameters<History['pushState']>
  ): void {
    originalPush.apply(this, args)
    record()
  }
  history.replaceState = function patchedReplace(
    this: History,
    ...args: Parameters<History['replaceState']>
  ): void {
    originalReplace.apply(this, args)
    record()
  }
  window.addEventListener('popstate', record)

  return () => {
    history.pushState = originalPush
    history.replaceState = originalReplace
    window.removeEventListener('popstate', record)
  }
}

const noop = (): void => {}

function safeHref(): string {
  return typeof location !== 'undefined' ? location.href : ''
}
